import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ActionColumnRenderCell, actionColumnModel } from '@shared/dataGrid/columns/actionColumn'
import { can } from '@utils/permissions'
import { BaseDataGrid } from '@shared/dataGrid/BaseDataGrid'
import { IconButton, Tooltip } from '@mui/material'
import { themeIcons } from '@theme/icons'
import { useNewTabHook } from '@hooks/useNewTabHook'

import { UserColumnType, UsersRowModel, useUsersContext } from '../provider/UsersProvider'

import { userDataGridColumns } from './userDataGridColumns'

export const UserDataGrid: React.FC = () => {
  const { t } = useTranslation()

  const navigate = useNewTabHook()

  const {
    updateSelectedIds,
    handleDelete,
    handleSendInvite,
    updateSelectedItem,
    loading,
    userData,
    selectedIds,
    paginationModel,
    updatePaginationModel,
    sortModel,
    updateSortModel,
    updateInitialState,
    filterModel,
    updateFilterModel
  } = useUsersContext()

  const rowData = React.useMemo(() => {
    return userData?.edges?.map(({ node }) => node) || []
  }, [userData])

  const onRowSelectionChange = React.useCallback((params: GridRowSelectionModel) => {
    updateSelectedIds(params)
  }, [])

  const handleDeleteRow = React.useCallback(async (row: UsersRowModel) => {
    updateSelectedItem(row)
    handleDelete(false)
  }, [])

  const rowCountState = React.useMemo(() => userData?.totalCount ?? 0, [userData])

  const columnData = React.useMemo<UserColumnType[]>(() => {
    return [...userDataGridColumns, {
      ...actionColumnModel,
      renderCell: (params: any) => (
        <ActionColumnRenderCell
          {...(can('users:create') && {
            additionalActions: (
              <Tooltip title={t('tooltips.sendInvitation')}>
                <IconButton onClick={() => handleSendInvite(params.row.id)}>
                  {params.row.active ? <themeIcons.Drafts sx={{ width: 20, height: 20 }} /> : <themeIcons.Mail sx={{ width: 20, height: 20 }} />}
                </IconButton>
              </Tooltip>
            )
          })}
          {...(can('users:write') && { onEdit: () => navigate(`/users/${params.row.id}`) })}
          {...(can('users:delete') && { onDelete: () => handleDeleteRow(params.row) })}
        />
      )
    } as UserColumnType].map((column) => {
      return {
        disableColumnMenu: true,
        headerName: column.hideHeader ? '' : t(`users.column.${column.field}`),
        ...column
      }
    })
  }, [])

  return (
    <BaseDataGrid
      columns={columnData}
      gridKey='users'
      onInitialState={updateInitialState}
      rows={rowData}
      rowCount={rowCountState}
      paginationModel={paginationModel}
      paginationMode='server'
      autoPageSize={false}
      onPaginationModelChange={updatePaginationModel}
      sortModel={sortModel ? [sortModel] : []}
      sortingMode='server'
      onSortModelChange={updateSortModel}
      filterModel={filterModel}
      filterMode='server'
      onFilterModelChange={updateFilterModel}
      loading={loading}
      rowSelectionModel={selectedIds}
      rowSelection
      checkboxSelection
      onRowSelectionModelChange={onRowSelectionChange}
    />
  )
}
